import { useEffect, useState } from "react";
import {
  setMonitoringQueueData,
  setMonitoringQueueFilters,
  setMonitoringQueueSort,
  setMonitoringQueueTotalCount,
  useSelectFiltersMonitoringQueue,
  useSelectPageMonitoringQueue,
  useSelectSortMonitoringQueue,
  useSelectSizeMonitoringQueue,
  setMonitoringQueuePage,
  setMonitoringQueueSize,
  useSelectTotalCountMonitoringQueue,
} from "../../../../redux/features/monitoringQueue/monitoringQueueSlice";
import { SORT_DIRECTION } from "../../../../utils/vars";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { getMonitoringQueue } from "../../../../services/monitoringQueue";
import { useCheckPermission } from "../../../../hooks/useCheckPermission";
import { useQueryParams } from "../../../../hooks/useQueryParams";

const FILTER_KEYS = [
  "id",
  "ruleName",
  "personNames",
  "contractIdentifier",
  "assignee",
  "status",
  "resolution",
  "startDate",
  "endDate",
];

const useMonitoringQueue = () => {
  const dispatch = useDispatch();
  const filters = useSelectFiltersMonitoringQueue();
  const sort = useSelectSortMonitoringQueue();
  const page = useSelectPageMonitoringQueue();
  const size = useSelectSizeMonitoringQueue();
  const totalCount = useSelectTotalCountMonitoringQueue();
  const { params, adjustFilter } = useQueryParams();
  const resolutionTextPermission = useCheckPermission("RESOLUTION_TEXT");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const filterParams = {};
    FILTER_KEYS.forEach((key) => {
      if (params[key]) filterParams[key] = params[key];
    });

    if (Object.keys(filterParams).length > 0) {
      dispatch(setMonitoringQueueFilters(filterParams));
    }
    if (params.page) dispatch(setMonitoringQueuePage(Number(params.page)));
    if (params.size) dispatch(setMonitoringQueueSize(Number(params.size)));
    if (params.direction && params.sortType) {
      dispatch(
        setMonitoringQueueSort({
          direction: params.direction,
          type: params.sortType,
        }),
      );
    }

    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching: isFetchingMQ } = useQuery({
    queryKey: ["get-monitoring-queue", getQueryParams()],
    queryFn: () => getMonitoringQueue(getQueryParams()),
    enabled: ready,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) {
      dispatch(setMonitoringQueueData(data?.data));
      dispatch(setMonitoringQueueTotalCount(data?.totalCount));
    }
  }, [data, dispatch]);

  const toggleSort = (type) => {
    const currentType = sort.type;
    const currentDirection = sort.direction;
    const direction =
      type === currentType && currentDirection === SORT_DIRECTION.DESC
        ? SORT_DIRECTION.ASC
        : SORT_DIRECTION.DESC;

    dispatch(setMonitoringQueueSort({ direction, type }));
    dispatch(setMonitoringQueuePage(0));
    adjustFilter("direction", direction);
    adjustFilter("sortType", type);
    adjustFilter("page", null);
  };

  const handleFilterChange = async (key, filter) => {
    if (key !== "DATE") {
      dispatch(setMonitoringQueueFilters({ [key]: filter }));
      adjustFilter(key, filter || null);
    } else {
      dispatch(setMonitoringQueueFilters({ ...filter }));
      Object.entries(filter).forEach(([k, v]) => adjustFilter(k, v || null));
    }
    dispatch(setMonitoringQueuePage(0));
    adjustFilter("page", null);
  };

  function getQueryParams() {
    const searchParams = new URLSearchParams();
    searchParams.append("page", page);
    searchParams.append("size", size);
    searchParams.append("direction", sort.direction);
    searchParams.append("type", sort.type);

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        searchParams.append(key, value);
      }
    });

    return searchParams.toString();
  }

  const handlePageChange = (newPage) => {
    dispatch(setMonitoringQueuePage(newPage - 1));
    adjustFilter("page", newPage - 1 === 0 ? null : newPage - 1);
  };

  const handleSizeChange = (newSize) => {
    dispatch(setMonitoringQueueSize(newSize));
    adjustFilter("size", newSize === 20 ? null : newSize);
    adjustFilter("page", null);
    dispatch(setMonitoringQueuePage(0));
  };

  const onRowClick = (id) => {
    window.open(`/monitoring/flag-details/${id}`, "_blank");
  };

  return {
    sort,
    filters,
    size,
    isFetchingMQ,
    page,
    totalCount,
    resolutionTextPermission,
    handlePageChange,
    handleSizeChange,
    toggleSort,
    handleFilterChange,
    onRowClick,
  };
};

export default useMonitoringQueue;
